#import "src/components/OrgChart/queries/orgChartDirectRoster.graphql"
#import "src/queries/fragments/personCardFragment.graphql"

fragment TeamMembersFields on Group {
  id
  name
  kind
  type
  dailyRate @include(if: $includeOverlayTeamDailyRate)
  isHidden
  isDeleted
  isRootForSource
  enableSupplyStructureEdits
  supplyStructureRoot {
    id
    name
    isRootForSource
    enableSupplyStructureEdits
  }
  directParentId
  hierarchyIds # needed for move group modal
  memberCount
  directMemberCount
  childTeamCount
  directChildTeamCount
  # Card badge totals. positionCount rolls descendants up like memberCount;
  # directPositionCount is this team's own seats, which is what decides whether
  # an expanded team draws a positions box.
  positionCount @include(if: $includePositionCounts)
  positionFilledCount @include(if: $includePositionCounts)
  directPositionCount @include(if: $includePositionCounts)
  supplyOwners @include(if: $includeOwners) {
    aggregateId
    isVacancy
    ownerPosition {
      id
      name
    }
    firstName
    surname
    preferredName
    avatar
    jobTitle
    fte
    # Tags for reporting-line owner cards when owner ∉ team members
    tags: appliedTags {
      id
      tagId
      type
      displayValue
      isActive
      attributes
      entityTagAttributes
    }
    memberships(input: { membershipTypes: [OWNER] }) {
      currentCompositeId
      groupId
      ownerRole
    }
    supplyOwnerOf {
      id
      name
      type
      hierarchy
      hierarchyIds
      parentIds
    }
    memberOf {
      id
      name
      type
      hierarchy
      hierarchyIds
      parentIds
    }
  }
  supplyInferredOwnedBy {
    ...PersonCardFragment
  }
  attributes {
    id
    attributeType
    subtype
    label
    value
  }
  permissions {
    updateGroupAttributes
    canMutateGroup
  }
  tags: appliedTags {
    id
    tagId
    type
    displayValue
    isActive
    attributes
    tag {
      tagAttributesMetaData {
        person {
          aggregateId
          preferredName
          firstName
          surname
          avatar
        }
      }
    }
  }
  ...OrgChartDirectRosterFields
}
fragment ChildTeamListFields on Group {
  id
  name
  kind
  type
  dailyRate @include(if: $includeOverlayTeamDailyRate)
  isHidden
  isDeleted
  isRootForSource
  enableSupplyStructureEdits
  supplyStructureRoot {
    id
    name
    isRootForSource
    enableSupplyStructureEdits
  }
  directParentId
  hierarchyIds # needed for move group modal
  memberCount
  childTeamCount
  directMemberCount
  directChildTeamCount
  positionCount @include(if: $includePositionCounts)
  positionFilledCount @include(if: $includePositionCounts)
  directPositionCount @include(if: $includePositionCounts)
  supplyOwners @include(if: $includeOwners) {
    aggregateId
    isVacancy
    ownerPosition {
      id
      name
    }
    firstName
    surname
    preferredName
    avatar
    jobTitle
    fte
    # Tags for reporting-line owner cards when owner ∉ team members
    tags: appliedTags {
      id
      tagId
      type
      displayValue
      isActive
      attributes
      entityTagAttributes
    }
    memberships(input: { membershipTypes: [OWNER] }) {
      currentCompositeId
      groupId
      ownerRole
    }
    supplyOwnerOf {
      id
      name
      type
      hierarchy
      hierarchyIds
      parentIds
    }
    memberOf {
      id
      name
      type
      hierarchy
      hierarchyIds
      parentIds
    }
  }
  supplyInferredOwnedBy {
    ...PersonCardFragment
  }
  attributes {
    id
    attributeType
    subtype
    label
    value
  }
  tags: appliedTags {
    id
    tagId
    type
    displayValue
    isActive
    attributes
    tag {
      tagAttributesMetaData {
        person {
          aggregateId
          preferredName
          firstName
          surname
          avatar
        }
      }
    }
  }
  permissions {
    updateGroupAttributes
    canMutateGroup
    canMutateMemberships
  }
}

query getTeamAndMembersAndChildTeamsQuery(
  $groupId: String!
  $includeMembers: Boolean = true
  $rosterPage: Int = 1
  $rosterPageSize: Int = 100
  $rosterExcludePositions: Boolean = false
  $membersExcludeHidden: Boolean = true
  $membersExcludeDeleted: Boolean = true
  $includeTeams: Boolean = true
  $teamsLimit: Int = 500
  $teamsOffset: Int = 0
  $includeOwners: Boolean = false
  $includePositionFields: Boolean = false
  # @hasRole'd fields — the directive throws FORBIDDEN, so a directory user must
  # not select them. Gated apart from $includePositionFields for that reason.
  $includePositionCounts: Boolean = false
  $includeOverlayTeamDailyRate: Boolean = false
  $includeOverlayAllAttributes: Boolean = false
  $includeOverlayPersonFields: Boolean = false
) {
  team: getGroup(groupId: $groupId) {
    ...TeamMembersFields
    childTeams(limit: $teamsLimit, offset: $teamsOffset)
      @include(if: $includeTeams) {
      ...ChildTeamListFields
    }
  }
}

query getTeamAndMembersAndChildTeamsWithOptionQuery(
  $groupId: String!
  $includeMembers: Boolean = true
  $rosterPage: Int = 1
  $rosterPageSize: Int = 100
  $rosterExcludePositions: Boolean = false
  $membersExcludeHidden: Boolean = true
  $membersExcludeDeleted: Boolean = true
  $includeTeams: Boolean = true
  $teamsLimit: Int = 500
  $teamsOffset: Int = 0
  $includeOwners: Boolean = false
  $includePositionFields: Boolean = false
  # @hasRole'd fields — the directive throws FORBIDDEN, so a directory user must
  # not select them. Gated apart from $includePositionFields for that reason.
  $includePositionCounts: Boolean = false
  $includeOverlayTeamDailyRate: Boolean = false
  $includeOverlayAllAttributes: Boolean = false
  $includeOverlayPersonFields: Boolean = false
  $optionId: ID
) {
  team: getGroupWithOption(groupId: $groupId, optionId: $optionId) {
    ...TeamMembersFields
    childTeams(limit: $teamsLimit, offset: $teamsOffset)
      @include(if: $includeTeams) {
      ...ChildTeamListFields
    }
  }
}
